import { FC, useEffect, useState } from 'react';
import {
  Row, Col, Typography, Button, message, Spin,
} from 'taxaroo-ui';
import WorkforceIcon from 'src/assets/icons/workforce.svg';
import { useCreateBillingPortalSessionMutation } from '~src/graphql/mutations/stripeInformation';
import { useCurrentStripeInformationQuery } from '~src/graphql/queries/settings';
import { useCreateFirmAccountAddOnMutation } from '~src/graphql/mutations/add-ons';
import { useGetStripeCustomerQuery } from '~src/graphql/queries/stripeInformation';
import { GetFirmAccountAddOnByNameQuery, useGetFirmAccountAddOnByNameQuery } from '~src/graphql/queries/addOns';
import { StripeSubscriptionStatus } from '~src/graphql';
import { useUpdateFirmAccountAddOnMutation } from '~src/graphql/mutations/addOns';
import * as styles from './styles.module.css';
import { AiAssistantPlanType } from '.';
import {
  basicModalContent, basicModalHeader, chatOnlyModalContent,
  chatOnlyModalHeader, premiumModalContent, premiumModalHeader,
  returnDefaultModalContent, returnDefaultModalHeader,
} from './ModalElements';

const { Text } = Typography;

type AddonsModalContentProps = {
  subscriptionCreated: (plan: AiAssistantPlanType) => void;
  subscriptionCancelled: () => void;
  plan: AiAssistantPlanType;
  setPlan: (plan: AiAssistantPlanType) => void;
};

export type PlanStatus = 'active' | 'cancelsAtEndOfBillingCycle' | 'none';

export type PlanStatusMap = {
  [key in AiAssistantPlanType]: PlanStatus;
};

const AddonsModalContent: FC<AddonsModalContentProps> = ({
  subscriptionCreated,
  subscriptionCancelled,
  plan,
  setPlan,
}) => {
  const [modalHeader, setModalHeader] = useState<JSX.Element>();
  const [modalContent, setModalContent] = useState<JSX.Element>();
  const [planStatuses, setPlanStatuses] = useState<PlanStatusMap>({
    'AI Chat': 'none',
    'AI VideoPlus': 'none',
    'AI VideoPro': 'none',
  });
  const { data: currentStripeInformation, loading: loadingCustomer } = useCurrentStripeInformationQuery({
    onError: (err) => message.error(err.message),
  });
  const { customerId } = currentStripeInformation?.CurrentStripeInformation || {};
  const [createBillingPortalSession] = useCreateBillingPortalSessionMutation({
    onError: (err) => message.error(err.message),
  });

  // Add this function to reset the component state
  const resetComponent = () => {
    setPlan(undefined);
  };

  const [createAddon, { loading: loadingCreateAddon }] = useCreateFirmAccountAddOnMutation({
    onError: (err) => message.error(err.message),
    onCompleted: () => {
      subscriptionCreated(plan);
      resetComponent(); // Reset after subscription is created
    },
    refetchQueries: ['GetFirmAccountAddOnByName'],
  });
  const [updateFirmAccountAddOn, { loading: loadingUpdateFirmAccountAddOn }] = useUpdateFirmAccountAddOnMutation({
    onError: (err) => message.error(err.message),
    onCompleted: () => {
      subscriptionCancelled();
      resetComponent();
    },
  });

  const { data: customerData, loading: loadingStripeCustomer } = useGetStripeCustomerQuery({
    variables: {
      getStripeCustomerInput: {
        customerId,
      },
    },
    skip: !customerId,
  });
  const customer = customerData?.getStripeCustomer;
  const defaultPaymentMethod = customer?.invoice_settings?.default_payment_method || customer?.default_source;

  const { data: firmAccountChatOnlyAddOn } = useGetFirmAccountAddOnByNameQuery({
    variables: { name: 'taxaroo-ai-chat-only-plan' },
  });
  const { data: firmAccountBasicAddOn } = useGetFirmAccountAddOnByNameQuery({
    variables: { name: 'taxaroo-ai-basic-plan' },
  });
  const { data: firmAccountPremiumAddOn } = useGetFirmAccountAddOnByNameQuery({
    variables: { name: 'taxaroo-ai-premium-plan' },
  });

  const getPlanStatus = (addOn: GetFirmAccountAddOnByNameQuery) => {
    if (!addOn?.firmAccountAddOnByName?.id) {
      return 'none';
    }
    if (addOn?.firmAccountAddOnByName?.subscriptionCancelAtPeriodEnd) {
      return 'cancelsAtEndOfBillingCycle';
    }
    if (addOn?.firmAccountAddOnByName?.subscriptionStatus === StripeSubscriptionStatus.Active) {
      return 'active';
    }
    return 'none';
  };

  const updatePlanStatuses = () => {
    setPlanStatuses({
      'AI Chat': getPlanStatus(firmAccountChatOnlyAddOn),
      'AI VideoPlus': getPlanStatus(firmAccountBasicAddOn),
      'AI VideoPro': getPlanStatus(firmAccountPremiumAddOn),
    });
  };

  // useEffect to update plan statuses
  useEffect(() => {
    updatePlanStatuses();
  }, [
    firmAccountBasicAddOn?.firmAccountAddOnByName?.subscriptionStatus,
    firmAccountBasicAddOn?.firmAccountAddOnByName?.subscriptionCancelAtPeriodEnd,
    firmAccountPremiumAddOn?.firmAccountAddOnByName?.subscriptionStatus,
    firmAccountPremiumAddOn?.firmAccountAddOnByName?.subscriptionCancelAtPeriodEnd,
    firmAccountChatOnlyAddOn?.firmAccountAddOnByName?.subscriptionStatus,
    firmAccountChatOnlyAddOn?.firmAccountAddOnByName?.subscriptionCancelAtPeriodEnd,
  ]);

  const handleUpdatePaymentMethod = async () => {
    const { data } = await createBillingPortalSession({
      variables: {
        createBillingPortalInput: {
          customer: customerId,
          return_url: window.location.href,
          flow_data: {
            type: 'payment_method_update',
          },
        },
      },
    });

    window.location.href = data.createBillingPortalSession;
  };

  const getAddonIdByName = (addOnName: AiAssistantPlanType) => {
    switch (addOnName) {
      case 'AI VideoPlus':
        return 'b84fe2cc-a288-4513-870f-46be84a8b9ad';
      case 'AI VideoPro':
        return '7b8571cc-9a10-4db3-88d1-06693c1de18b';
      case 'AI Chat':
        return 'ff305e16-4603-4d3f-89bf-3566bdcdd91f';
      default:
        return null;
    }
  };

  const getFirmAccountAddOnIdByName = (addOnName: AiAssistantPlanType) => {
    switch (addOnName) {
      case 'AI VideoPlus':
        return firmAccountBasicAddOn?.firmAccountAddOnByName?.id;
      case 'AI VideoPro':
        return firmAccountPremiumAddOn?.firmAccountAddOnByName?.id;
      case 'AI Chat':
        return firmAccountChatOnlyAddOn?.firmAccountAddOnByName?.id;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (plan === 'AI VideoPlus') {
      setModalHeader(basicModalHeader);
      setModalContent(basicModalContent);
    } else if (plan === 'AI VideoPro') {
      setModalHeader(premiumModalHeader);
      setModalContent(premiumModalContent);
    } else if (plan === 'AI Chat') {
      setModalHeader(chatOnlyModalHeader);
      setModalContent(chatOnlyModalContent);
    } else {
      setModalHeader(returnDefaultModalHeader(defaultPaymentMethod, handleUpdatePaymentMethod));
      setModalContent(returnDefaultModalContent(defaultPaymentMethod, setPlan, planStatuses));
    }
  }, [plan, currentStripeInformation, defaultPaymentMethod, planStatuses?.['AI Chat'], planStatuses?.['AI VideoPlus'], planStatuses?.['AI VideoPro']]);

  return (
    <div>
      {loadingStripeCustomer || loadingCustomer ? (
        <Row align="middle" justify="center" style={{ height: '600px' }}>
          <Spin size="large" />
        </Row>
      ) : (
        <>
          {modalHeader}
          {modalContent}
          <Row justify="space-between" align="middle" style={{ width: `${(22 / 24) * 100}%` }}>
            <Col xs={24} md={12} className={styles.poweredByModal}>
              Powered by &nbsp;&nbsp;
              <WorkforceIcon className={styles.poweredByIcon} />
              &nbsp;
              <Text className={styles.poweredByCompanyName}>Workforce AI</Text>
            </Col>
            {Boolean(plan) && (
              <Col xs={24} md={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="default"
                  onClick={() => setPlan(undefined)}
                  className={styles.modalNavDefault}
                >
                  Go Back
                </Button>
                {planStatuses?.[plan] === 'active' ? (
                  <Button
                    type="danger"
                    className={styles.modalNavPrimary}
                    loading={loadingUpdateFirmAccountAddOn}
                    onClick={async () => {
                      await updateFirmAccountAddOn({
                        variables: {
                          input: {
                            id: getFirmAccountAddOnIdByName(plan),
                            active: false,
                          },
                        },
                      });
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    className={styles.modalNavPrimary}
                    loading={loadingCreateAddon}
                    onClick={async () => {
                      await createAddon({
                        variables: {
                          input: {
                            addOnId: getAddonIdByName(plan),
                          },
                        },
                      });
                    }}
                  >
                    Pay
                  </Button>
                )}
              </Col>
            )}
          </Row>
        </>
      )}
    </div>
  );
};

export default AddonsModalContent;
